import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  ActionKey,
  type OperationalCompletionRequest,
  type OperationalCompletionResult,
  type TournamentDto,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore, sessionVenueKey } from '../../store/auth.store';
import { useDialogFocus } from '../../hooks/useDialogFocus';

function savedRequest(key: string): OperationalCompletionRequest | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    if (
      value &&
      typeof value === 'object' &&
      'operationKey' in value &&
      typeof value.operationKey === 'string' &&
      'reason' in value &&
      typeof value.reason === 'string' &&
      'acknowledgeNoPosting' in value &&
      value.acknowledgeNoPosting === true
    )
      return value as OperationalCompletionRequest;
  } catch {
    /* Corrupt or unavailable browser storage is not authoritative evidence. */
  }
  return null;
}

export default function OperationalCompletionControl({
  tournament,
}: {
  tournament: TournamentDto;
}) {
  const auth = useAuthStore();
  const key = `operational-completion:${sessionVenueKey(auth.accessToken)}:${auth.user?.id}:${tournament.id}`;
  return <Control key={key} tournament={tournament} storageKey={key} />;
}

function Control({ tournament, storageKey }: { tournament: TournamentDto; storageKey: string }) {
  const { t } = useTranslation('tournament');
  const actions = useAuthStore((s) => s.allowedActions);
  const system = useAuthStore((s) => s.user?.isSystemUser);
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(() => savedRequest(storageKey));
  const [reason, setReason] = useState(request?.reason ?? '');
  const [ack, setAck] = useState(request?.acknowledgeNoPosting ?? false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const focus = useDialogFocus(open);
  const mutation = useMutation({
    mutationFn: (body: OperationalCompletionRequest) =>
      api.post<OperationalCompletionResult>(
        `/tournament/${tournament.id}/operational-complete`,
        body,
      ),
    retry: false,
    onSuccess: () => {
      sessionStorage.removeItem(storageKey);
      setRequest(null);
      setOpen(false);
      void client.invalidateQueries({ queryKey: ['tournament'] });
      void client.invalidateQueries({ queryKey: ['tournaments'] });
    },
    onError: (failure: Error) => {
      const response = isAxiosError(failure) ? failure.response : undefined;
      const prefix = 'OPERATIONAL_COMPLETION_BLOCKED_';
      const errorCode =
        typeof response?.data?.errorCode === 'string' ? response.data.errorCode : '';
      const category = errorCode.startsWith(prefix) ? errorCode.slice(prefix.length) : null;
      setError(
        category
          ? t('operationalCompletion.blocked', {
              category: t(`operationalCompletion.categories.${category}`, {
                defaultValue: t('operationalCompletion.categories.UNKNOWN'),
              }),
            })
          : t('operationalCompletion.retryError'),
      );
      // A definitive rejection has not committed this request. Network/5xx failures keep
      // the exact persisted body so a lost successful response can be safely replayed.
      if (response && response.status >= 400 && response.status < 500 && response.status !== 409) {
        sessionStorage.removeItem(storageKey);
        setRequest(null);
      }
    },
    onSettled: () => {
      submitting.current = false;
    },
  });
  if (tournament.operationalCompletion)
    return (
      <p role="status" className="mb-4 border border-success bg-success/10 p-4 text-base-content">
        {t('operationalCompletion.completed')}
      </p>
    );
  if (!system && !actions?.includes(ActionKey.TOURNAMENT_COMPLETE)) return null;
  if (
    !tournament.manualCountMode ||
    tournament.type !== 'ELIMINATION' ||
    tournament.payoutType !== 'STANDARD' ||
    !['IN_PROGRESS', 'PAUSED'].includes(tournament.status)
  )
    return null;
  const dismiss = () => {
    if (!mutation.isPending) {
      setOpen(false);
      if (!request) {
        setReason('');
        setAck(false);
        setError('');
      }
    }
  };
  return (
    <>
      <button
        type="button"
        className="mb-4 min-h-11 bg-primary px-4 text-primary-content"
        onClick={() => setOpen(true)}
      >
        {t('operationalCompletion.action')}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-base-content/40 p-4"
          onKeyDown={(event) => {
            if (event.key === 'Escape') dismiss();
          }}
        >
          <div
            ref={focus}
            role="dialog"
            aria-modal="true"
            aria-labelledby="operational-completion-title"
            tabIndex={-1}
            className="max-h-[90dvh] w-full max-w-lg overflow-y-auto border border-base-300 bg-base-100 p-6 text-base-content shadow-xl"
          >
            <h2 id="operational-completion-title" className="text-xl font-bold">
              {t('operationalCompletion.action')}
            </h2>
            <p className="my-3 font-medium">{tournament.name}</p>
            <p className="mb-4 text-sm">{t('operationalCompletion.explanation')}</p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (submitting.current || mutation.isPending || !reason.trim() || !ack) return;
                const body = request ?? {
                  operationKey: crypto.randomUUID(),
                  reason: reason.trim(),
                  acknowledgeNoPosting: true,
                };
                try {
                  sessionStorage.setItem(storageKey, JSON.stringify(body));
                } catch {
                  setError(t('operationalCompletion.storageError'));
                  return;
                }
                submitting.current = true;
                setRequest(body);
                setError('');
                mutation.mutate(body);
              }}
            >
              <label className="block text-sm" htmlFor="operational-reason">
                {t('operationalCompletion.reason')}
              </label>
              <textarea
                id="operational-reason"
                maxLength={500}
                required
                disabled={!!request || mutation.isPending}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                className="my-2 w-full border border-base-300 bg-base-200 p-3"
              />
              <label className="my-3 flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  required
                  checked={ack}
                  disabled={!!request || mutation.isPending}
                  onChange={(event) => setAck(event.target.checked)}
                />
                <span>{t('operationalCompletion.acknowledgement')}</span>
              </label>
              <p className="my-3 text-sm text-base-content/70">
                {t('operationalCompletion.check')}
              </p>
              {error && (
                <p role="alert" className="my-3 text-error">
                  {error}
                </p>
              )}
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  disabled={mutation.isPending}
                  onClick={dismiss}
                  className="min-h-11 border border-base-300 px-4"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!reason.trim() || !ack || mutation.isPending}
                  className="min-h-11 bg-primary px-4 text-primary-content disabled:opacity-50"
                >
                  {mutation.isPending
                    ? t('operationalCompletion.pending')
                    : request
                      ? t('operationalCompletion.retry')
                      : t('operationalCompletion.confirm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
